import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';

// Pages
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import QuestionsPage from './pages/QuestionsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ExamPage from './pages/ExamPage';
import AIPage from './pages/AIPage';
import ImportPage from './pages/ImportPage';
import SubscriptionPage from './pages/SubscriptionPage';
import ContactPage from './pages/ContactPage';
import AdminPage from './pages/AdminPage';
import CRMPage from './pages/CRMPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminEmailPage from './pages/AdminEmailPage';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import ReportedIssuesPage from './pages/ReportedIssuesPage';
import JobsPage from './pages/JobsPage';
import ResearchPage from './pages/ResearchPage';
import DiagnosticPage from './pages/DiagnosticPage';
import DiagnosticTestPage from './pages/DiagnosticTestPage';
import DiagnosticResultsPage from './pages/DiagnosticResultsPage';
import UniformsPage from './pages/UniformsPage';
import TutoringPage from './pages/TutoringPage';
import TutorRegistrationPage from './pages/TutorRegistrationPage';
import AdminTutoringPage from './pages/AdminTutoringPage';
import AdminJobBoardsPage from './pages/AdminJobBoardsPage';
import AdminGearPage from './pages/AdminGearPage';
import AdminQuestionsPage from './pages/AdminQuestionsPage';
import OnboardingPage from './pages/OnboardingPage';
import StudyPlanPage from './pages/StudyPlanPage';

// Tenant Loading Screen
const TenantLoadingScreen = () => {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.is_admin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Plan Route Component - Requires paid subscription (free users redirected to Plans)
const PlanRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Admin always has access
  if (user?.is_admin) {
    return children;
  }

  // Check if user has a paid subscription
  const tier = user?.subscription_plan || user?.subscription_tier || 'free';
  const status = user?.subscription_status || 'free';
  const isPaid = status !== 'free' && tier !== 'free' && (status === 'active' || status === 'free_grant');

  if (!isPaid) {
    return <Navigate to="/subscription" state={{ upgrade: true }} replace />;
  }

  return children;
};

// Home Route - Shows Landing for guests, Dashboard for authenticated users
// New users who haven't completed onboarding get redirected to the wizard
const HomeRoute = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <LandingPage />;

  // Check if user needs onboarding (new users who haven't completed the wizard)
  if (user && user.onboarding_completed === false) {
    return <Navigate to="/onboarding" replace />;
  }

  return <HomePage />;
};

// Main App Routes
function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route
        path="/"
        element={<HomeRoute />}
      />
      <Route
        path="/questions"
        element={
          <ProtectedRoute>
            <QuestionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <PlanRoute>
            <AnalyticsPage />
          </PlanRoute>
        }
      />
      <Route
        path="/study-plan"
        element={
          <ProtectedRoute>
            <StudyPlanPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/exam"
        element={
          <PlanRoute>
            <ExamPage />
          </PlanRoute>
        }
      />
      <Route
        path="/ai"
        element={
          <PlanRoute>
            <AIPage />
          </PlanRoute>
        }
      />
      <Route
        path="/import"
        element={
          <PlanRoute>
            <ImportPage />
          </PlanRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contact"
        element={
          <ProtectedRoute>
            <ContactPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs"
        element={
          <PlanRoute>
            <JobsPage />
          </PlanRoute>
        }
      />
      <Route
        path="/research"
        element={
          <PlanRoute>
            <ResearchPage />
          </PlanRoute>
        }
      />
      <Route
        path="/uniforms"
        element={
          <PlanRoute>
            <UniformsPage />
          </PlanRoute>
        }
      />
      <Route
        path="/tutoring"
        element={
          <PlanRoute>
            <TutoringPage />
          </PlanRoute>
        }
      />
      <Route
        path="/tutoring/register"
        element={
          <PlanRoute>
            <TutorRegistrationPage />
          </PlanRoute>
        }
      />
      <Route
        path="/diagnostic"
        element={
          <PlanRoute>
            <DiagnosticPage />
          </PlanRoute>
        }
      />
      <Route
        path="/diagnostic/results"
        element={
          <PlanRoute>
            <DiagnosticResultsPage />
          </PlanRoute>
        }
      />
      <Route
        path="/diagnostic/:testId"
        element={
          <PlanRoute>
            <DiagnosticTestPage />
          </PlanRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/crm"
        element={
          <AdminRoute>
            <CRMPage />
          </AdminRoute>
        }
      />
      {/* Public Routes - No Auth Required */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Admin Sub-Routes */}
      <Route
        path="/admin/email"
        element={
          <AdminRoute>
            <AdminEmailPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/reported-issues"
        element={
          <AdminRoute>
            <ReportedIssuesPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <AdminRoute>
            <AdminAnalyticsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/tutoring"
        element={
          <AdminRoute>
            <AdminTutoringPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/job-boards"
        element={
          <AdminRoute>
            <AdminJobBoardsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/gear"
        element={
          <AdminRoute>
            <AdminGearPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/questions"
        element={
          <AdminRoute>
            <AdminQuestionsPage />
          </AdminRoute>
        }
      />
    </Routes>
  );
}

// App with Tenant Loading
function AppContent() {
  const { isLoading } = useTenant();
  
  if (isLoading) {
    return <TenantLoadingScreen />;
  }
  
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <TenantProvider>
        <AppContent />
      </TenantProvider>
    </ThemeProvider>
  );
}

export default App;
